/**
 * Signing out a public session that has been walked away from.
 *
 * The daemon's twelve hours are the outer bound and they are not a protection:
 * the failure this covers is somebody finishing at a library machine, closing
 * the *window* rather than signing out, and the next person opening the browser
 * to a mailbox. Twelve hours is plenty of time for that; twenty minutes of
 * nobody touching the keyboard is not.
 *
 * # Only in a public session
 *
 * Deliberately. An idle timeout on somebody's own laptop is a client that signs
 * them out while they read a long message in another window, and the usual
 * answer to that -- watch for scrolling, watch for visibility, extend it to an
 * hour -- is a pile of heuristics bought to fix a problem nobody had. The
 * person who wants this ticked a box that says so.
 *
 * # Why pointer and key events, and not activity in the app
 *
 * Because the question is whether a *person* is here, not whether the app is
 * doing anything. Mail arriving, a poll completing and a notification landing
 * are all things that happen to an empty chair. `pointerdown` and `keydown` on
 * the window are the two events that cannot happen without one, they bubble
 * from everywhere in the page, and neither fires often enough for the reset to
 * cost anything.
 *
 * They are listened for on the window rather than the document so the message
 * frame's own events still count on the way up -- and the one gap is worth
 * naming: the reader's `<iframe>` is sandboxed and cross-origin, so a person
 * scrolling *inside* a message with the mouse never reaches this. Scrolling is
 * not one of the two events anyway, so the gap costs nothing today; it would
 * matter to anyone who adds `wheel` to the list.
 */

import { effect } from "@preact/signals";

import { authState, logout } from "./auth";
import { isPublicSession } from "./publicSession";

/**
 * Long enough to read a message, short enough to matter.
 *
 * Exported for the test rather than for a caller. It is not a setting: a
 * public-computer timeout somebody can lengthen is a public-computer timeout
 * that gets lengthened.
 */
export const IDLE_MS = 20 * 60 * 1000;

let timer: ReturnType<typeof setTimeout> | null = null;
let listening = false;

function bump(): void {
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    // Swallowed, and only here. `logout()` re-throws whatever the daemon did so
    // that a person who pressed a button gets told -- but nobody pressed this
    // one, there is no screen to put the message on, and an unhandled rejection
    // from a timer is a console entry with no context at all. The half that
    // matters happened regardless: the `finally` has already cleared the state
    // and the storage.
    void logout().catch(() => {});
  }, IDLE_MS);
}

function arm(): void {
  if (!listening) {
    listening = true;
    // `passive` because neither listener ever calls `preventDefault`, and a
    // non-passive `pointerdown` on the window is a scroll-jank hazard on a
    // phone for no reason at all.
    window.addEventListener("pointerdown", bump, { passive: true });
    window.addEventListener("keydown", bump, { passive: true });
  }
  bump();
}

function disarm(): void {
  if (listening) {
    listening = false;
    window.removeEventListener("pointerdown", bump);
    window.removeEventListener("keydown", bump);
  }
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/**
 * Watch for the mode and the session together, and return a way to stop.
 *
 * The two conditions are read in one effect because either one going false has
 * to tear the timer down: a session that ended has nothing left to protect, and
 * a timer left running past it would call `logout()` a second time on a client
 * that is already signed out.
 *
 * `isPublicSession()` is a function and not a signal, so this effect wakes on
 * `authState` alone -- which is enough, because the flag is only ever set
 * immediately before the state becomes "in" and cleared as part of it becoming
 * "out". Worth knowing before adding a third way to enter the mode.
 */
export function watchIdle(): () => void {
  const stop = effect(() => {
    if (authState.value === "in" && isPublicSession()) arm();
    else disarm();
  });
  return () => {
    stop();
    disarm();
  };
}
